<template>
  <div class="flex items-center gap-2">
    <Icon
      v-if="isStale"
      name="mdi-clock-alert-outline"
      :size="15"
      color="#FFBC68"
    />
    <span v-if="statusLabel" class="text-xs transition-colors duration-200" :class="statusClass">
      {{ statusLabel }}
    </span>
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger as-child>
          <button
            type="button"
            class="flex size-9 items-center justify-center rounded-lg text-primary transition-colors duration-200 hover:bg-lightPurple-lightest focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-default"
            :aria-label="label"
            :disabled="loading"
            @click="emit('refresh')"
          >
            <Icon
              :name="loading ? 'mdi-loading' : 'mdi-refresh'"
              :size="19"
              color="#3513e2"
              :class="{'animate-spin': loading}"
            />
          </button>
        </TooltipTrigger>
        <TooltipContent class="bg-primary font-medium" side="left">
          <p>{{ label }}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  </div>
</template>

<script setup lang="ts">
import {computed, ref, watch} from "vue";
import dayjs from "dayjs";
import dateHelper from "@/core/helpers/dateHelper";
import Icon from "@/core/components/icon/Icon.vue";
import {Tooltip, TooltipContent, TooltipProvider, TooltipTrigger} from "@/core/components/ui/tooltip";

const props = withDefaults(defineProps<{
    /** The tooltip and accessible label of the button. */
    label: string;
    /** Whether a refresh is running. */
    loading: boolean;
    /** When the served data was computed. No label is displayed without it. */
    cachedAt?: string | null;
    /** Prefix of the resting label, i.e. "Données du" or "Liste du". */
    datePrefix?: string;
    /** Timestamp of the last successful refresh, to confirm it briefly. */
    refreshedAt?: number | null;
}>(), {
    cachedAt: null,
    datePrefix: 'Données du',
    refreshedAt: null
});

const emit = defineEmits<{ (e: 'refresh'): void }>();

/** How long the success confirmation stays visible. */
const CONFIRMATION_DURATION = 5000;
/** Above that age, the data is flagged as outdated. */
const STALE_HOURS = 24;
/**
 * A computation before that hour comes from the nightly cache job, which
 * runs at 04:00. Its time of day carries no information for the pharmacist
 * and reads as stale, so only the date is displayed — no reason to refresh.
 */
const NIGHTLY_JOB_UNTIL_HOUR = 8;

const confirming = ref(false);
let confirmationTimeout: ReturnType<typeof setTimeout> | undefined;

watch(() => props.refreshedAt, (refreshedAt) => {
    if (!refreshedAt) {
        return;
    }

    confirming.value = true;
    clearTimeout(confirmationTimeout);
    confirmationTimeout = setTimeout(() => {
        confirming.value = false;
    }, CONFIRMATION_DURATION);
});

const isStale = computed(() => {
    if (!props.cachedAt || props.loading || confirming.value) {
        return false;
    }

    return dayjs().diff(dayjs(props.cachedAt), 'hour') >= STALE_HOURS;
});

const statusLabel = computed(() => {
    if (props.loading) {
        return 'Actualisation…';
    }

    if (confirming.value) {
        return 'Mis à jour à l\'instant';
    }

    if (!props.cachedAt) {
        return null;
    }

    const computedByNightlyJob = Number(dateHelper.shortFormat(props.cachedAt, 'H')) < NIGHTLY_JOB_UNTIL_HOUR;
    const format = computedByNightlyJob ? 'DD/MM' : 'DD/MM [à] HH[ h ]mm';

    return `${props.datePrefix} ${dateHelper.shortFormat(props.cachedAt, format)}`;
});

const statusClass = computed(() => props.loading ? 'text-primary' : 'text-gray-500');
</script>
